// src/pages/Admin/Blogs/BlogView.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';
import { ref, deleteObject } from 'firebase/storage';
import { db, storage } from '../../../db/firebase';
import { ArrowLeft, Edit, Trash2, Calendar, User, Tag, Eye, Clock } from 'lucide-react';

// Fixed Markdown Renderer Component with proper image handling
const MarkdownRenderer = ({ content }) => {
  const renderMarkdown = (text) => {
    if (!text) return '<p class="text-gray-500 italic">No content available.</p>';
    
    let html = text;

    // Process images FIRST - before any other processing
    const imageRegex = /!\[(.*?)\]\((.*?)\)/g;
    html = html.replace(imageRegex, '<div class="my-8 flex justify-center"><img src="$2" alt="$1" class="max-w-full h-auto rounded-lg shadow-lg mx-auto border" loading="lazy" /></div>');

    // Now process other markdown elements
    html = html
      // Headers
      .replace(/^### (.*$)/gim, '<h3 class="text-2xl font-bold mt-8 mb-4 text-gray-900">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-3xl font-bold mt-10 mb-6 text-gray-900">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-4xl font-bold mt-12 mb-8 text-gray-900">$1</h1>')
      // Bold and italic
      .replace(/\*\*\*(.*?)\*\*\*/gim, '<strong><em>$1</em></strong>')
      .replace(/\*\*(.*?)\*\*/gim, '<strong class="font-bold">$1</strong>')
      .replace(/\*(.*?)\*/gim, '<em class="italic">$1</em>')
      // Underline
      .replace(/<u>(.*?)<\/u>/gim, '<u class="underline">$1</u>')
      // Links - only match actual http/https links
      .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/gim, '<a href="$2" class="text-blue-600 hover:text-blue-800 hover:underline font-medium" target="_blank" rel="noopener noreferrer">$1</a>')
      // Blockquotes
      .replace(/^> (.*$)/gim, '<blockquote class="border-l-4 border-purple-500 bg-purple-50 pl-6 py-4 my-6 text-gray-700 italic">$1</blockquote>')
      // Code blocks
      .replace(/```([^`]+)```/gim, '<pre class="bg-gray-800 text-gray-100 p-4 rounded-lg my-6 overflow-x-auto"><code class="font-mono text-sm">$1</code></pre>')
      // Inline code
      .replace(/`([^`]+)`/gim, '<code class="bg-gray-100 px-2 py-1 rounded text-sm font-mono border">$1</code>')
      // Line breaks
      .replace(/\n/g, '<br>');

    // Handle lists
    const lines = html.split('<br>');
    let inList = false;
    let listType = '';
    
    const processedLines = lines.map(line => {
      // Skip processing if line contains images (already processed)
      if (line.includes('<img') || line.includes('class="my-8 flex justify-center"')) {
        if (inList) {
          inList = false;
          return `</${listType}>${line}`;
        }
        return line;
      }
      
      // Bullet lists
      if (line.trim().startsWith('- ')) {
        if (!inList || listType !== 'ul') {
          inList = true;
          listType = 'ul';
          return `<ul class="list-disc ml-8 my-4 space-y-2"><li class="text-gray-700">${line.trim().substring(2)}</li>`;
        }
        return `<li class="text-gray-700">${line.trim().substring(2)}</li>`;
      }
      
      // Numbered lists
      if (/^\d+\. .*$/.test(line.trim())) {
        if (!inList || listType !== 'ol') {
          inList = true;
          listType = 'ol';
          return `<ol class="list-decimal ml-8 my-4 space-y-2"><li class="text-gray-700">${line.trim().replace(/^\d+\.\s*/, '')}</li>`;
        }
        return `<li class="text-gray-700">${line.trim().replace(/^\d+\.\s*/, '')}</li>`;
      }
      
      // Close list if we were in one
      if (inList && line.trim() === '') {
        inList = false;
        return `</${listType}><br>`;
      }
      
      if (inList) {
        inList = false;
        return `</${listType}>${line}`;
      }
      
      return line;
    });

    html = processedLines.join('<br>');
    
    // Close any open list
    if (inList) {
      html += `</${listType}>`;
    }

    // Wrap in paragraph if no block elements
    const hasBlockElements = /<(h[1-6]|ul|ol|blockquote|pre|div)/
      .test(html) || html.includes('<img') || html.includes('class="my-8 flex justify-center"');
    
    if (!hasBlockElements) {
      html = html.split('<br><br>').join('</p><p class="my-4 text-gray-700 leading-relaxed">');
      html = `<p class="my-4 text-gray-700 leading-relaxed">${html}</p>`;
    }

    return html;
  };

  return (
    <div 
      className="prose prose-lg max-w-none"
      dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
    />
  );
};

const BlogView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [contentImages, setContentImages] = useState([]);

  useEffect(() => {
    fetchBlog();
  }, [id]);

  const fetchBlog = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'blogs', id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const blogData = { id: docSnap.id, ...docSnap.data() };
        setBlog(blogData);
        
        // Extract images from content for preloading/counting
        const images = extractImagesFromContent(blogData.content);
        setContentImages(images);
      } else {
        alert('Blog not found!');
        navigate('/admin/blogs');
      }
    } catch (error) {
      console.error('Error fetching blog:', error);
      alert('Error loading blog');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${blog.title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeleteLoading(true);
      
      // Delete featured image from storage if exists
      if (blog.featuredImage) {
        try {
          const imageRef = ref(storage, blog.featuredImage);
          await deleteObject(imageRef);
        } catch (error) {
          console.error('Error deleting image:', error);
        }
      }

      // Delete content images from storage
      for (const imageUrl of contentImages) {
        try {
          const imageRef = ref(storage, imageUrl);
          await deleteObject(imageRef);
        } catch (error) {
          console.error('Error deleting content image:', error);
        }
      }

      // Delete blog document
      await deleteDoc(doc(db, 'blogs', id));
      
      alert('Blog deleted successfully!');
      navigate('/admin/blogs');
    } catch (error) {
      console.error('Error deleting blog:', error);
      alert('Error deleting blog');
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp.seconds * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getReadingTime = (content) => {
    if (!content) return '0 min';
    const wordsPerMinute = 200;
    const words = content.split(/\s+/).length;
    const minutes = Math.ceil(words / wordsPerMinute);
    return `${minutes} min read`;
  };

  // Function to extract all images from content
  const extractImagesFromContent = (content) => {
    if (!content) return [];
    const imageRegex = /!\[.*?\]\((.*?)\)/g;
    const images = [];
    let match;
    while ((match = imageRegex.exec(content)) !== null) {
      images.push(match[1]);
    }
    return images;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
            
            <div className="h-64 bg-gray-200 rounded mb-6"></div>
            
            <div className="space-y-3">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-600 mb-4">Blog Not Found</h2>
          <p className="text-gray-500 mb-6">The blog post you're looking for doesn't exist.</p>
          <Link
            to="/admin/blogs"
            className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors inline-flex items-center gap-2"
          >
            <ArrowLeft size={20} />
            Back to Blogs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Blog Details</h1>
            <p className="text-gray-600 mt-2">View and manage blog post content</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/admin/blogs"
              className="bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-2"
            >
              <ArrowLeft size={20} />
              Back to Blogs
            </Link>
            <Link
              to={`/admin/blogs/edit/${id}`}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Edit size={20} />
              Edit Blog
            </Link>
            <button
              onClick={handleDelete}
              disabled={deleteLoading}
              className="bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <Trash2 size={20} />
              {deleteLoading ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>

        {/* Blog Content Card */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          {/* Featured Image */}
          {blog.featuredImage && (
            <div className="h-96 overflow-hidden relative bg-gray-100">
              {imageLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
                </div>
              )}
              <img
                src={blog.featuredImage}
                alt={blog.title}
                className="w-full h-full object-cover transition-opacity duration-300"
                onLoad={() => setImageLoading(false)}
                onError={() => setImageLoading(false)}
                style={{ opacity: imageLoading ? 0 : 1 }}
              />
            </div>
          )}

          {/* Blog Content */}
          <div className="p-8">
            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Calendar size={16} />
                <span>Created: {formatDate(blog.createdAt)}</span>
              </div>
              {blog.updatedAt && blog.updatedAt.seconds !== blog.createdAt.seconds && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Calendar size={16} />
                  <span>Updated: {formatDate(blog.updatedAt)}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <User size={16} />
                <span>By {blog.author || 'Admin'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Clock size={16} />
                <span>{getReadingTime(blog.content)}</span>
              </div>
              <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                blog.status === 'published' 
                  ? 'bg-green-100 text-green-800 border border-green-200'
                  : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
              }`}>
                {blog.status === 'published' ? '📢 Published' : '📝 Draft'}
              </div>
            </div>

            {/* Title */}
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              {blog.title}
            </h1>

            {/* Excerpt */}
            {blog.excerpt && (
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8 rounded-r-lg">
                <p className="text-lg text-blue-800 leading-relaxed">{blog.excerpt}</p>
              </div>
            )}

            {/* Tags */}
            {blog.tags && blog.tags.length > 0 && (
              <div className="flex items-center gap-3 mb-8">
                <Tag size={18} className="text-gray-500 flex-shrink-0" />
                <div className="flex flex-wrap gap-2">
                  {blog.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="bg-gradient-to-r from-purple-100 to-blue-100 text-purple-800 px-3 py-2 rounded-full text-sm font-medium border border-purple-200"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Content */}
            <div className="border-t border-gray-100 pt-8">
              <MarkdownRenderer content={blog.content} />
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <span className="text-2xl">📄</span>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{blog.content?.length || 0}</div>
                <div className="text-sm text-gray-600">Characters</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-100 rounded-lg">
                <span className="text-2xl">📝</span>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {blog.content ? blog.content.split(/\s+/).length : 0}
                </div>
                <div className="text-sm text-gray-600">Words</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-100 rounded-lg">
                <span className="text-2xl">🖼️</span>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">
                  {contentImages.length + (blog.featuredImage ? 1 : 0)}
                </div>
                <div className="text-sm text-gray-600">Total Images</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-100 rounded-lg">
                <span className="text-2xl">⏱️</span>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900 capitalize">
                  {getReadingTime(blog.content)}
                </div>
                <div className="text-sm text-gray-600">Reading Time</div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="flex flex-wrap gap-4">
            <Link
              to={`/admin/blogs/edit/${id}`}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Edit size={20} />
              Edit This Blog
            </Link>
            <Link
              to="/admin/blogs/create"
              className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <span className="text-lg">+</span>
              Create New Blog
            </Link>
            <button
              onClick={() => window.print()}
              className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <span className="text-lg">🖨️</span>
              Print Preview
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogView;